// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorHeader Component
// =============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { Bell, Settings, HelpCircle, ExternalLink, Menu } from 'lucide-react';
import { useVendorProfile } from '@/features/vendor/hooks/useVendorProfile';
import { useSidebarCollapsed, useToggleSidebar } from '@/features/vendor/stores/vendorStore';

interface VendorHeaderProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export function VendorHeader({ title, subtitle, actions }: VendorHeaderProps) {
  const { data: profile } = useVendorProfile();
  const collapsed = useSidebarCollapsed();
  const toggleSidebar = useToggleSidebar();

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <button
          onClick={toggleSidebar}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Toggle menu"
        >
          <Menu className="w-5 h-5 text-navy" />
        </button>

        {/* Page Title */}
        {title && (
          <div>
            <h1 className="font-display text-xl font-semibold text-navy">{title}</h1>
            {subtitle && <p className="text-sm text-warm-gray">{subtitle}</p>}
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* Page Actions */}
        {actions && <div className="mr-4">{actions}</div>}

        {/* View Store Link */}
        {profile?.slug && (
          <Link
            to={`/marketplace/vendors/${profile.slug}`}
            target="_blank"
            className="hidden md:flex items-center gap-2 px-3 py-2 text-sm font-medium text-navy hover:bg-cream-dark rounded-lg transition-colors"
          >
            View Storefront
            <ExternalLink className="w-4 h-4" />
          </Link>
        )}

        {/* Help */}
        <button
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Help"
        >
          <HelpCircle className="w-5 h-5 text-warm-gray" />
        </button>

        {/* Notifications */}
        <button
          className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-warm-gray" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-terracotta rounded-full" />
        </button>

        {/* Settings */}
        <Link
          to="/vendor/profile"
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Settings"
        >
          <Settings className="w-5 h-5 text-warm-gray" />
        </Link>

        {/* Profile */}
        <div className="ml-2 pl-4 border-l border-gray-200">
          <Link
            to="/vendor/profile"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            {profile?.logoUrl ? (
              <img
                src={profile.logoUrl}
                alt={profile.name}
                className="w-8 h-8 rounded-lg object-cover"
              />
            ) : (
              <div className="w-8 h-8 bg-terracotta rounded-lg flex items-center justify-center">
                <span className="text-sm font-semibold text-white">
                  {profile?.name?.charAt(0) || 'V'}
                </span>
              </div>
            )}
            <div className="hidden lg:block">
              <p className="text-sm font-medium text-navy">
                {profile?.name || 'Vendor'}
              </p>
            </div>
          </Link>
        </div>
      </div>
    </header>
  );
}

export default VendorHeader;

